/**
 * Maps each sidebar entry (by path or by parent label) to the permission key
 * that gates its visibility. Used by Sidebar.js + ModuleGate.js.
 *
 * Keys follow the same convention as the backend catalog:
 *   "<module_key>.<tab_key>.view"
 *
 * If a path is not in this map, no gating is applied (always visible).
 * Super Admin (permissions = ["*"]) bypasses gating entirely.
 */
export const PATH_TO_PERMISSION = {
  '/finance/payments': 'finance.payments.view',
  '/dashboard': 'dashboard.overview.view',

  '/ess/portal': 'ess.portal.view',
  '/employee-profile': 'ess.my_profile.view',
  '/ess/statutory-forms': 'ess.statutory_forms.view',
  '/ess/leave-attendance': 'ess.leave_attendance.view',
  '/ess/pay-tax': 'ess.pay_tax.view',
  '/ess/expense-claims': 'ess.expense_claims.view',
  '/ess/documents': 'ess.documents.view',
  '/ess/helpdesk': 'helpdesk.my_tickets.view',
  '/helpdesk/tracker': 'helpdesk.tracker.view',

  '/attendance/upload': 'attendance_ops.upload.view',
  '/attendance/history': 'attendance_ops.history.view',
  '/attendance/internal': 'attendance_ops.internal.view',
  '/attendance/calculation': 'attendance_ops.calculation.view',
  '/attendance/reports': 'attendance_ops.reports.view',

  '/leave-policy': 'leave_policy.master.view',
  '/leave-policy/balances': 'leave_policy.balances.view',

  '/admin/data-reset': 'admin_tools.data_reset.view',
  '/admin/processing-guide': 'help_guide.processing_guide.view',

  '/manager/dashboard': 'manager.dashboard.view',
  '/manager/approvals': 'manager.approvals.view',

  '/hr-workbench': 'hr_workbench.inbox.view',
  '/hr-bench': 'hr_bench.workbench.view',
  '/hr/increment-workbench': 'increment_workbench.workbench.view',
  '/advance-settlements': 'advance_settlements.workbench.view',
  '/analytics': 'analytics.overview.view',
  '/reports': 'reports.overview.view',
  '/finance': 'finance.overview.view',
  '/business-controls': 'business_controls.overview.view',
  '/employees': 'employees.list.view',
  '/hr/ess-approvals': 'employees.ess_approvals.view',
  '/payroll/kyc-cycles': 'employees.kyc_cycles.view',
  '/payroll/kyc-corrections': 'employees.kyc_corrections.view',
  '/leave': 'leave.management.view',

  '/attendance': 'attendance.dashboard.view',
  '/attendance/daily': 'attendance.daily.view',
  '/attendance/monthly': 'attendance.monthly.view',
  '/attendance/punches': 'attendance.punches.view',
  '/attendance/biometric': 'attendance.biometric.view',
  '/attendance/biometric/mapping': 'attendance.biometric_mapping.view',
  '/attendance/biometric/import': 'attendance.biometric_import.view',
  '/attendance/face-logs': 'attendance.face_logs.view',
  '/attendance/face-punch': 'attendance.face_punch.view',
  '/attendance/geofence': 'attendance.geofence.view',
  '/attendance/exception-register': 'attendance.exception_register.view',
  '/attendance/roster': 'attendance.roster.view',
  '/attendance/regularization': 'attendance.regularization.view',
  '/attendance/ot-approval': 'attendance.ot_approval.view',

  '/payroll/setup': 'payroll.setup.view',
  '/payroll/wage-master': 'payroll.wage_master.view',
  '/monthly-inputs': 'payroll.monthly_inputs.view',
  '/payroll/structures': 'payroll.structures.view',
  '/payroll/ctc-planner': 'payroll.ctc_planner.view',
  '/payroll/employee-ctc-upload': 'payroll.employee_ctc_upload.view',
  '/payroll/legacy-imports': 'payroll.legacy_imports.view',
  '/payroll/run': 'payroll.run.view',
  '/payroll/exceptions': 'payroll.exceptions.view',
  '/payroll/approval': 'payroll.approval.view',
  '/payroll/payslips': 'payroll.payslips.view',
  '/payroll/fnf': 'payroll.fnf.view',
  '/payroll/arrears': 'payroll.arrears.view',
  '/payroll/loans': 'payroll.loans.view',
  '/payroll/salary-revision': 'payroll.salary_revision.view',
  '/payroll/reimbursements-tracker': 'payroll.reimbursements_tracker.view',
  '/payroll/reimbursement-policy': 'payroll.reimbursement_policy.view',
  '/payroll/expense-claims-admin': 'payroll.expense_claims_admin.view',
  '/payroll/approval-workflows': 'payroll.approval_workflows.view',
  '/line-item-approval/dashboard': 'payroll.line_item_dashboard.view',
  '/line-item-approval/inbox': 'payroll.line_item_inbox.view',
  '/line-item-approval/workflows': 'payroll.line_item_workflows.view',

  '/statutory/setup': 'statutory.setup.view',
  '/statutory/state-rates': 'statutory.state_rates.view',
  '/statutory/masters': 'statutory.masters.view',
  '/statutory/eligibility': 'statutory.eligibility.view',
  '/statutory/declarations': 'statutory.declarations.view',
  '/statutory/tds-projection': 'statutory.tds_projection.view',
  '/statutory/working': 'statutory.working.view',
  '/statutory/challans': 'statutory.challans.view',
  '/statutory/register': 'statutory.register.view',
  '/statutory/tax-worksheet': 'statutory.tax_worksheet.view',
  '/statutory/compliance': 'statutory.compliance.view',

  '/masters/setup': 'masters.setup.view',
  // Stage 73.20ek — leaf paths that were previously missing from the map.
  // Missing entries default to VISIBLE in canViewPath(), which was letting
  // items leak through for custom-role users. Fill them in explicitly.
  '/masters/tax-slabs': 'masters.tax_slabs.view',
  '/masters/asset-document-types': 'masters.asset_document_types.view',
  '/payroll/loan-policy': 'masters.loan_policy.view',
  '/hr-bench/reports': 'reports.hr_bench_reports.view',
  '/finance/advance-ledger': 'payroll.advance_ledger_admin.view',
  '/reports/tds-validation': 'reports.tds_validation.view',
  '/reports/bank-transfer-letter': 'reports.bank_transfer_letter.view',
  '/attendance/reports': 'reports.attendance_reports.view',
  '/advance-settlements/reports': 'reports.advance_settlement_reports.view',
  '/ess/advance-ledger': 'ess.advance_ledger.view',
  '/ess/reimbursements': 'ess.reimbursements.view',
  '/ess/loans': 'ess.advance.view',
  '/ess/kyc': 'ess.kyc.view',
  '/ess/assets-docs': 'ess.assets_docs.view',
  '/ess/user-guide': 'ess.user_guide.view',
  '/ess/statutory-forms': 'ess.statutory_forms.view',
  '/consolidated-approval': 'payroll.consolidated_approval.view',
  '/companies': 'companies.list.view',
  '/organization': 'organization.tree.view',
  '/users': 'users.list.view',
  '/roles': 'roles.matrix.view',
  '/audit-logs': 'audit.logs.view',
  '/notifications': 'notifications.list.view',
  '/action-inbox': 'action_inbox.list.view',
  '/profile': 'profile.me.view',

  // Travel Desk — full RBAC-integrated module
  // Keys match the backend CATALOG tab keys under the 'travel_desk' module.
  '/travel':                      'travel_desk.dashboard.view',
  '/travel/dashboard':            'travel_desk.dashboard.view',
  '/travel/requests':             'travel_desk.requests.view',
  '/travel/request/new':          'travel_desk.new_request.view',
  '/travel/request/:id':          'travel_desk.request_detail.view',
  '/travel/request/:id/edit':     'travel_desk.edit_request.view',
  '/travel/approval':             'travel_desk.approval.view',
  '/travel/approvals':            'travel_desk.approval.view',
  '/travel/booking':              'travel_desk.booking.view',
  '/travel/bookings':             'travel_desk.booking.view',
  '/travel/cab-booking':          'travel_desk.cab_booking.view',
  '/travel/cabs-hotels':          'travel_desk.cab_booking.view',
  '/travel/hotel-booking':        'travel_desk.hotel_booking.view',
  '/travel/policy':               'travel_desk.policy.view',
  '/travel/policies':             'travel_desk.policy.view',
  '/travel/policy-mapping':       'travel_desk.policy_mapping.view',
  '/travel/budget':               'travel_desk.budget.view',
  '/travel/budgets':              'travel_desk.budget.view',
  '/travel/credit-cards':         'travel_desk.credit_cards.view',
  '/travel/vendors':              'travel_desk.vendors.view',
  '/travel/reports':              'travel_desk.reports.view',
  '/travel/settings':             'travel_desk.settings.view',
  '/ess/travel-requests':         'travel_desk.my_requests.view',
};

/**
 * Returns true if the user can `view` the given path. Super Admin (`*`)
 * bypasses. If the path isn't in the gating map, treat as public FOR
 * BUILT-IN roles (backward compat). Stage 73.20ek — Custom-role users
 * hit an unmapped path? Deny. This prevents "custom role sees everything"
 * regression when someone adds a new leaf path but forgets to register
 * its permission key here.
 */
export const canViewPath = (user, path) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const perms = user.permissions || [];
  if (perms.includes('*')) return true;
  let required = PATH_TO_PERMISSION[path];
  if (!required) {
    const matchedKey = Object.keys(PATH_TO_PERMISSION).find((pattern) => {
      if (!pattern.includes(':')) return false;
      const regexStr = '^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$';
      return new RegExp(regexStr).test(path);
    });
    if (matchedKey) {
      required = PATH_TO_PERMISSION[matchedKey];
    }
  }
  if (!required) {
    // Custom-role users: STRICT — unmapped ⇒ deny.
    if (typeof user.role === 'string' && user.role.startsWith('custom:')) return false;
    // Built-in roles: unmapped ⇒ allow (backward compat).
    return true;
  }
  return perms.includes(required);
};

/**
 * Returns true if the user has the given permission key.
 * Use for in-page gating: `if (!hasPermission(user, 'payroll.run.edit')) ...`
 */
export const hasPermission = (user, key) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const perms = user.permissions || [];
  if (perms.includes('*')) return true;
  return perms.includes(key);
};
